import type { Injector, WritableSignal } from '@angular/core';
import type { AbstractControl, ValidatorFn } from '@angular/forms';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxComponentInputs,
  OnyxCustomToastOptions,
  OnyxDropdownOptions,
  OnyxHttpErrorResponse,
  OnyxMaybeArray,
  OnyxOption,
  OnyxOptionsGroup,
  OnyxTab,
} from '@onyx/angular';
import {
  ActionHelper,
  FormHelper,
  OnyxToastMessage,
  OnyxToastService,
} from '@onyx/angular';
import { castArray, chain } from 'lodash';
import { DateTime } from 'luxon';
import { filter, firstValueFrom, startWith } from 'rxjs';
import { environment } from '../../../config/environment';
import { AuthStore } from '../../auth/common/stores/auth.store';
import { IntercomService } from '../../dashboard/common/services/intercom.service';
import { ValidationTooltipComponent } from '../components/tooltips/validation-tooltip/validation-tooltip.component';
import type { DictionaryCode } from '../enums/dictionary-code';
import { ValidationSeverity } from '../enums/validation-severity';
import type { ValidationField } from '../interfaces/validation/validation-field';
import type { ValidationIssue } from '../interfaces/validation/validation-issue';
import type { ValidationResult } from '../interfaces/validation/validation-result';
import type { DictionariesService } from '../services/dictionaries.service';

export class ValidationHelper {
  public static toggleControls(
    controls: OnyxMaybeArray<AbstractControl>,
    options: { enabled: boolean; reset: boolean },
  ): void {
    options.enabled
      ? this.enableControls(controls)
      : this.disableControls(controls, options);
  }

  public static enableControls(
    controls: OnyxMaybeArray<AbstractControl>,
  ): void {
    for (const control of castArray(controls)) {
      control.enable();
    }
  }

  public static disableControls(
    controls: OnyxMaybeArray<AbstractControl>,
    options: { reset: boolean },
  ): void {
    for (const control of castArray(controls)) {
      control.disable();
      if (options.reset) FormHelper.reset(control);
    }
  }

  public static toggleValidators(
    controls: OnyxMaybeArray<AbstractControl>,
    validators: OnyxMaybeArray<ValidatorFn>,
    enabled: boolean,
  ): void {
    enabled
      ? this.addValidators(controls, validators)
      : this.removeValidators(controls, validators);
  }

  public static toggleRequiredValidator(
    controls: OnyxMaybeArray<AbstractControl>,
    required: boolean,
  ): void {
    ValidationHelper.toggleValidators(controls, Validators.required, required);
  }

  public static addValidators(
    controls: OnyxMaybeArray<AbstractControl>,
    validators: OnyxMaybeArray<ValidatorFn>,
  ): void {
    for (const control of castArray(controls)) {
      control.addValidators(castArray(validators));
      control.updateValueAndValidity();
    }
  }

  public static removeValidators(
    controls: OnyxMaybeArray<AbstractControl>,
    validators: OnyxMaybeArray<ValidatorFn>,
  ): void {
    for (const control of castArray(controls)) {
      control.removeValidators(castArray(validators));
      control.updateValueAndValidity();
    }
  }

  public static clearValidators(
    controls: OnyxMaybeArray<AbstractControl>,
  ): void {
    for (const control of castArray(controls)) {
      control.clearValidators();
      control.updateValueAndValidity();
    }
  }

  public static async checkValidity(
    controls: OnyxMaybeArray<AbstractControl>,
    injector: Injector | null,
  ): Promise<boolean> {
    await this.waitForPendingValidators(controls);

    const submitDeep = (control: AbstractControl) => {
      if (control instanceof FormGroup) {
        for (const child of Object.values(control.controls)) {
          submitDeep(child);
        }
        return;
      } else if (control instanceof FormArray) {
        for (const child of control.controls) submitDeep(child);
        return;
      }

      if (control.disabled) return;

      control.markAsTouched({ onlySelf: true });
      control.markAsDirty({ onlySelf: true });

      if (control.asyncValidator != null && !control.pending) {
        control.setErrors(control.errors);
        return;
      }

      control.updateValueAndValidity({ onlySelf: true });
    };
    for (const control of castArray(controls)) submitDeep(control);

    await this.waitForPendingValidators(controls);

    if (!this.hasInvalidControls(controls)) return true;

    chain(document.querySelectorAll('.ng-invalid'))
      .filter((element) => !element.querySelectorAll('.ng-invalid')?.length)
      .orderBy((element) => element.getBoundingClientRect().top)
      .first()
      .tap((element) =>
        element?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        }),
      )
      .value();

    const toastService = injector?.get(OnyxToastService);
    toastService?.showCustom(OnyxToastMessage.INVALID_DATA, {
      buttons: [
        {
          text: 'labels.copyErrors',
          callback: () => {
            const errors: string[] = [];

            const catchErrors = (control: AbstractControl, name = 'root') => {
              if (control.errors) {
                const errorString = JSON.stringify(control.errors, null, 2);
                errors.push(`${name}: ${errorString}`);
              }

              if (control instanceof FormGroup) {
                for (const [key, child] of Object.entries(control.controls)) {
                  const path = name === 'root' ? key : `${name}.${key}`;
                  catchErrors(child, path);
                }
              } else if (control instanceof FormArray) {
                for (let i = 0; i < control.controls.length; i++) {
                  const path = `${name}[${i}]`;
                  catchErrors(control.controls[i], path);
                }
              }
            };

            const rootControls = castArray(controls);
            for (const control of rootControls) catchErrors(control);

            ActionHelper.copy(JSON.stringify(errors, null, 2), toastService);
          },
          variant: 'ghost',
          color: 'black',
          size: 24,
        },
      ],
    });

    return false;
  }

  public static updateControl(
    control: AbstractControl,
    field: ValidationField,
  ): void {
    switch (field.type) {
      case 'conditional':
        break;

      case 'forbidden':
        control.disable();
        FormHelper.reset(control);
        break;

      case 'optional':
      case 'optional-in-list':
        control.enable();
        control.removeValidators(Validators.required);
        break;

      case 'required':
      case 'required-in-list':
        control.enable();
        control.addValidators(Validators.required);
        break;

      case 'required-in-range':
        control.enable();
        control.addValidators([
          Validators.required,
          Validators.min(field.min),
          Validators.max(field.max),
        ]);
        break;
    }

    control.updateValueAndValidity();
  }

  public static updateListControl(
    control: AbstractControl,
    field: ValidationField,
    translateService: TranslateService,
    dictionariesService: DictionariesService,
    dictionaryCode: DictionaryCode,
    options: WritableSignal<
      OnyxDropdownOptions<unknown> | OnyxTab<unknown>[] | null
    >,
    multiple: boolean,
  ): void {
    this.updateControl(control, field);
    options.set(null);

    if (field.type === 'forbidden') return;

    if (
      field.type === 'required-in-list' ||
      field.type === 'optional-in-list'
    ) {
      options.set(
        field.values.map((value) => {
          const key = `${dictionaryCode}.${value}`;

          let name = translateService.instant(key);
          if (name === key) name = value;

          return { name, value };
        }),
      );

      if (field.values.length === 1) {
        control.setValue(multiple ? field.values : field.values[0]);
        control.disable();
      } else {
        const controlValue = control.value;
        if (multiple) {
          control.setValue(
            controlValue?.filter((value: any) =>
              field.values.includes(value),
            ) ?? null,
          );
        } else if (!field.values.includes(controlValue)) {
          control.setValue(null);
        }
      }
    } else {
      dictionariesService
        .getDictionary(dictionaryCode)
        .subscribe((dictionary) => options.set(dictionary));
    }
  }

  public static handleUnexpectedResponse(
    response: OnyxHttpErrorResponse,
    injector: Injector,
    toastOptions?: OnyxCustomToastOptions,
  ): void {
    const authStore = injector.get(AuthStore);
    const translateService = injector.get(TranslateService);
    const toastService = injector.get(OnyxToastService);
    const intercomService = injector.get(IntercomService);

    const user = authStore.user();

    const debugInfo = {
      url: response.url,
      status: response.status,
      error: response.error,
      user: user
        ? {
            uuid: user.uuid,
            email: user.email,
            roles: authStore.roles() ?? user.roles,
            companyId: authStore.companyId(),
          }
        : null,
      userAgent: navigator.userAgent,
      environment: environment.type,
      timestamp: DateTime.now().toISO(),
    };
    const debugMessage = translateService.instant(
      'issues.reportErrorIntercomMessage',
      { debug: JSON.stringify(debugInfo, null, 2) },
    );

    toastService.showCustom(OnyxToastMessage.SERVER_ERROR, {
      ...toastOptions,
      buttons: [
        ...(toastOptions?.buttons ?? []),
        {
          text: 'labels.reportError',
          callback: () => intercomService.startConversation(debugMessage),
          variant: 'ghost',
          color: 'black',
          size: 24,
        },
      ],
    });

    response.handled = true;
  }

  public static mapIssues<T = unknown>(
    issues: ValidationIssue[],
    option?: OnyxOption<T>,
  ): ValidationResult<T> {
    return chain(issues)
      .orderBy((issue) => issue.severity === ValidationSeverity.ERROR, 'desc')
      .thru((issues) => ({
        severity: issues.at(0)?.severity ?? null,
        issues,
        option: option ? ValidationHelper.mapOption(option, issues) : undefined,
      }))
      .value();
  }

  public static groupOptionsByIssue<T>(
    options: OnyxOption<T>[],
    subheadings?: {
      error?: string;
      valid?: string;
    },
  ): OnyxOptionsGroup<T>[] {
    return chain(options)
      .groupBy((option) => option?.hasIssue ?? false)
      .map((groupOptions, hasIssue) => ({
        options: groupOptions,
        issues: hasIssue === 'true',
      }))
      .filter(({ options }) => options.length > 0)
      .orderBy(({ issues }) => issues)
      .map(({ issues, options }) => ({
        id: !issues ? `0.valid` : `1.issues`,
        subheading: issues ? subheadings?.error : subheadings?.valid,
        options,
      }))
      .value();
  }

  private static async waitForPendingValidators(
    controls: OnyxMaybeArray<AbstractControl>,
  ): Promise<void> {
    const pendingControls: AbstractControl[] = [];

    const collectPending = (control: AbstractControl) => {
      if (control instanceof FormGroup) {
        for (const child of Object.values(control.controls)) {
          collectPending(child);
        }
      } else if (control instanceof FormArray) {
        for (const child of control.controls) collectPending(child);
      }

      if (control.pending) pendingControls.push(control);
    };

    for (const control of castArray(controls)) collectPending(control);
    if (!pendingControls.length) return;

    await Promise.all(
      pendingControls.map((control) =>
        firstValueFrom(
          control.statusChanges.pipe(
            startWith(control.status),
            filter((status) => status !== 'PENDING'),
          ),
        ),
      ),
    );
  }

  private static hasInvalidControls(
    controls: OnyxMaybeArray<AbstractControl>,
  ): boolean {
    let invalid = false;

    const checkDeep = (control: AbstractControl) => {
      if (control instanceof FormGroup) {
        for (const child of Object.values(control.controls)) checkDeep(child);
      } else if (control instanceof FormArray) {
        for (const child of control.controls) checkDeep(child);
      }

      if (control.invalid) invalid = true;
    };

    for (const control of castArray(controls)) checkDeep(control);

    return invalid;
  }

  private static mapOption<T = unknown>(
    option: OnyxOption<T>,
    issues: ValidationIssue[],
  ): OnyxOption<T> {
    const severity = issues.at(0)?.severity ?? null;
    return {
      ...option,
      ...(issues.length && {
        rightIcon: {
          name:
            severity === ValidationSeverity.ERROR
              ? 'clear-filled'
              : 'alert-two-tone-filled',
          size: 16,
        },
        rightIconColor: severity === ValidationSeverity.ERROR ? 'red' : 'black',
        rightIconTooltip: {
          ref: ValidationTooltipComponent,
          inputs: {
            issues,
          } satisfies OnyxComponentInputs<ValidationTooltipComponent>,
        },
        disabled: severity === ValidationSeverity.ERROR,
        hasIssue: severity != null,
      }),
    };
  }
}
